import { SPRITE } from "../art/cast";
import { type Ctx, dither, INK, pell, pline, ptext, R } from "../art/pixel";
import { clamp } from "../lib/util";

/**
 * Title-screen backdrop: the hospital at night, drawn at low resolution and
 * scaled up with crisp pixels. The pixel scale follows the window so a phone
 * in portrait gets a tall sky instead of a cropped wall of windows. Night-shift windows switch on and off, the
 * roof beacon blinks, an ambulance passes, and the cast walks along the
 * pavement in short "intermission" vignettes between attract loops.
 */

/** Street depth below the ground line (pavement + road), in scene pixels. */
const STREET = 34;

/** Deterministic pseudo-random in [0, 1). */
function hash(a: number, b = 0, c = 0): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

interface Star {
  x: number;
  y: number;
  phase: number;
  big: boolean;
}
interface Block {
  x: number;
  w: number;
  h: number;
}

/* ---------- intermissions ---------- */

interface Walker {
  k: string;
  /** "r" walks left→right, "l" right→left. */
  dir: "r" | "l";
  /** Seconds after the intermission starts. */
  t0: number;
  /** px per second. */
  speed: number;
  bubble?: "!" | "?" | "…";
}

/** Short pavement skits, played in order between quiet gaps. */
const SKITS: Walker[][] = [
  // Late again: the clerk sprints by, the attending follows wondering where they went.
  [
    { k: "clerk", dir: "r", t0: 0, speed: 72, bubble: "!" },
    { k: "vs", dir: "r", t0: 1.8, speed: 30, bubble: "?" },
  ],
  // Night ward: the senior nurse leads a mother-to-be and her husband in.
  [
    { k: "meiling", dir: "l", t0: 0, speed: 24 },
    { k: "yijun", dir: "l", t0: 0.9, speed: 17, bubble: "…" },
    { k: "husband", dir: "l", t0: 1.6, speed: 19 },
  ],
  // The surgical team heads for the OR.
  [
    { k: "gs_vs_or", dir: "r", t0: 0, speed: 30 },
    { k: "gs_r_or", dir: "r", t0: 0.7, speed: 30 },
    { k: "clerk_gown", dir: "r", t0: 1.4, speed: 30, bubble: "!" },
  ],
  // Reversal: now the attending runs, the clerk chases with the chart.
  [
    { k: "vs", dir: "l", t0: 0, speed: 64, bubble: "!" },
    { k: "clerk", dir: "l", t0: 0.6, speed: 64, bubble: "…" },
  ],
  // ER triage.
  [
    { k: "afen", dir: "r", t0: 0, speed: 26 },
    { k: "xiaoting_er", dir: "r", t0: 0.8, speed: 16, bubble: "…" },
    { k: "mother", dir: "r", t0: 1.3, speed: 18 },
  ],
];
const SKIT_GAP = 2.5;

function skitLength(skit: Walker[], width: number): number {
  return Math.max(...skit.map((w) => w.t0 + (width + 40) / w.speed)) + SKIT_GAP;
}

function bubble(c: Ctx, x: number, y: number, kind: "!" | "?" | "…", now: number): void {
  const bob = Math.floor(now / 350) % 2;
  y -= bob;
  R(c, INK, x - 1, y - 1, 12, 10);
  R(c, INK, x + 2, y + 8, 5, 3);
  const bg = kind === "?" ? "#ffd447" : kind === "!" ? "#ff5a6a" : "#ffffff";
  R(c, bg, x, y, 10, 8);
  R(c, bg, x + 3, y + 8, 3, 2);
  c.fillStyle = INK;
  if (kind === "!") {
    c.fillRect(x + 4, y + 1, 2, 4);
    c.fillRect(x + 4, y + 6, 2, 1);
  } else if (kind === "?") {
    c.fillRect(x + 3, y + 1, 4, 1);
    c.fillRect(x + 6, y + 2, 1, 2);
    c.fillRect(x + 4, y + 4, 2, 1);
    c.fillRect(x + 4, y + 6, 2, 1);
  } else {
    const k = Math.floor(now / 250) % 4;
    for (let i = 0; i < 3; i++) if (i < k) c.fillRect(x + 2 + i * 3, y + 4, 1, 1);
  }
}

/* ---------- scene ---------- */

export class TitleScene {
  private readonly c: Ctx;
  private W = 320;
  private H = 180;
  /** Ground line (top of the pavement). */
  private G = 146;
  private stars: Star[] = [];
  private skyline: Block[] = [];
  private lamps: number[] = [];

  constructor(private readonly cv: HTMLCanvasElement) {
    const c = cv.getContext("2d");
    if (!c) throw new Error("2d context unavailable");
    this.c = c;
  }

  /**
   * Pick a pixel scale for the window, then size the scene to fill it exactly.
   * `bottomInset` (CSS px) is covered by the footer, so the street sits above it.
   */
  resize(bottomInset = 0): void {
    const scale = clamp(Math.round(Math.min(innerWidth / 220, innerHeight / 180)), 1, 8);
    this.W = clamp(Math.round(innerWidth / scale), 160, 640);
    this.H = clamp(Math.round(innerHeight / scale), 150, 520);
    this.G = this.H - STREET - Math.round(bottomInset / scale);
    this.cv.width = this.W;
    this.cv.height = this.H;
    this.c.imageSmoothingEnabled = false;
    const W = this.W;
    this.stars = Array.from({ length: Math.round((W * this.G) / 650) }, (_, i) => ({
      x: Math.floor(hash(i, 1) * W),
      y: Math.floor(hash(i, 2) * (this.G - 42)),
      phase: hash(i, 3) * Math.PI * 2,
      big: hash(i, 4) > 0.9,
    }));
    this.skyline = [];
    for (let x = -6, i = 0; x < W; i++) {
      const w = 12 + Math.floor(hash(i, 7) * 18);
      this.skyline.push({ x, w, h: 18 + Math.floor(hash(i, 8) * 40) });
      x += w + 1;
    }
    this.lamps = [];
    for (let x = 18; x < W; x += 74) this.lamps.push(x);
  }

  /** x where the hospital's main block starts (kept roughly centred). */
  private get hx(): number {
    return Math.round(this.W / 2 - 64);
  }

  draw(now: number, still: boolean): void {
    const c = this.c;
    const t = still ? 0 : now;
    this.sky(c, t);
    this.skylineLayer(c, t);
    this.hospital(c, t);
    this.street(c);
    if (!still) {
      this.ambulance(c, now);
      this.walkers(c, now);
    }
  }

  private sky(c: Ctx, t: number): void {
    const W = this.W;
    const G = this.G;
    const b1 = Math.round(G * 0.38);
    const b2 = Math.round(G * 0.75);
    R(c, "#0b0f2a", 0, 0, W, b1);
    dither(c, 0, b1 - 8, W, 12, "#121845", 0.5);
    R(c, "#121845", 0, b1 + 4, W, b2 - b1 - 4);
    dither(c, 0, b2 - 6, W, 12, "#1b2462", 0.5);
    R(c, "#1b2462", 0, b2 + 6, W, G - b2 - 6);
    for (const s of this.stars) {
      const tw = Math.sin(t / 700 + s.phase);
      if (tw < -0.6) continue;
      const col = tw > 0.7 ? "#ffffff" : tw > 0 ? "#c9d1ff" : "#6f7bc4";
      R(c, col, s.x, s.y, 1, 1);
      if (s.big && tw > 0.8) {
        R(c, "#8f9be0", s.x - 1, s.y, 1, 1);
        R(c, "#8f9be0", s.x + 1, s.y, 1, 1);
        R(c, "#8f9be0", s.x, s.y - 1, 1, 1);
        R(c, "#8f9be0", s.x, s.y + 1, 1, 1);
      }
    }
    // Shooting star every ~11 s.
    const sp = t % 11000;
    if (t > 0 && sp < 700) {
      const k = sp / 700;
      const x = W * 0.85 - k * W * 0.4;
      const y = 10 + k * 34;
      pline(c, x, y, x + 9, y - 6, "#c9d1ff", 1);
      R(c, "#ffffff", Math.round(x), Math.round(y), 1, 1);
    }
    // Crescent moon.
    const mx = W - 38;
    pell(c, mx, 26, 10, 10, "#f4efc8");
    pell(c, mx + 5, 22, 9, 9, "#0b0f2a");
    // Clouds drift left.
    for (let i = 0; i < 3; i++) {
      const span = W + 70;
      const x = ((((hash(i, 11) * span - (t / 1000) * (2 + i)) % span) + span) % span) - 40;
      const y = Math.round(this.G * 0.25) + i * 19;
      pell(c, x, y, 16, 3, "#1e2655");
      pell(c, x + 10, y - 3, 9, 3, "#232c62");
      pell(c, x - 9, y + 1, 8, 2, "#1e2655");
    }
  }

  private skylineLayer(c: Ctx, t: number): void {
    this.skyline.forEach((b, i) => {
      R(c, "#141a3f", b.x, this.G - b.h, b.w, b.h);
      for (let y = this.G - b.h + 4; y < this.G - 4; y += 6)
        for (let x = b.x + 2; x < b.x + b.w - 2; x += 4) {
          const on = hash(i, x, y) > 0.86 && Math.floor(t / 9000 + hash(x, y)) % 4 !== 0;
          if (on) R(c, hash(x, y, 1) > 0.5 ? "#d9c27a" : "#3f4b8c", x, y, 1, 2);
        }
    });
  }

  private hospital(c: Ctx, t: number): void {
    const x0 = this.hx;
    const w = 128;
    const top = this.G - 98;
    // Wings.
    R(c, "#252c56", x0 - 36, this.G - 54, 36, 54);
    R(c, "#252c56", x0 + w, this.G - 62, 42, 62);
    R(c, "#323b70", x0 - 36, this.G - 54, 36, 2);
    R(c, "#323b70", x0 + w, this.G - 62, 42, 2);
    // Main block.
    R(c, "#2b3463", x0, top, w, 98);
    R(c, "#3b4580", x0, top, w, 3);
    R(c, "#232a52", x0 + w - 5, top + 3, 5, 95);
    // Windows: night shift — each window has its own on/off rhythm.
    const win = (x: number, y: number, i: number, j: number): void => {
      const period = 5000 + hash(i, j, 1) * 9000;
      const on = (Math.floor((t + hash(i, j, 2) * period) / period) + i * 3 + j) % 4 !== 0;
      const monitor = hash(i, j, 3) > 0.93;
      if (monitor) {
        const f = Math.floor(t / 120 + i) % 5 === 0;
        R(c, f ? "#7fd0ff" : "#4f9fe0", x, y, 6, 5);
      } else R(c, on ? (hash(i, j, 4) > 0.75 ? "#bfe3ff" : "#ffe3a1") : "#171c40", x, y, 6, 5);
      R(c, "#202754", x, y + 5, 6, 1);
    };
    for (let j = 0; j < 7; j++) for (let i = 0; i < 12; i++) win(x0 + 5 + i * 10, top + 12 + j * 11, i, j);
    for (let j = 0; j < 4; j++) for (let i = 0; i < 3; i++) win(x0 - 32 + i * 11, this.G - 46 + j * 11, i + 20, j);
    for (let j = 0; j < 5; j++) for (let i = 0; i < 3; i++) win(x0 + w + 5 + i * 12, this.G - 54 + j * 11, i + 30, j);
    // Roof: helipad, antenna with a blinking beacon, and the glowing red cross.
    R(c, "#3a4270", x0 + 84, top - 4, 30, 4);
    ptext(c, "H", x0 + 97, top - 9, "#d6dbf0");
    R(c, "#4a4f6e", x0 + 12, top - 16, 1, 16);
    const beacon = Math.floor(t / 600) % 2 === 0;
    R(c, beacon ? "#ff4d5e" : "#5a1a26", x0 + 11, top - 18, 3, 2);
    if (beacon) dither(c, x0 + 8, top - 21, 9, 8, "#ff4d5e", 0.18);
    // Glowing red cross on the right wing, clear of the logo above the main block.
    const cx = x0 + w + 15;
    const cy = this.G - 62 - 17;
    const pulse = 0.22 + 0.12 * Math.sin(t / 500);
    dither(c, cx - 6, cy - 6, 23, 23, "#ff4d5e", pulse);
    R(c, "#4a4f6e", cx + 5, cy + 11, 1, 6);
    R(c, "#ff4d5e", cx + 3, cy, 5, 11);
    R(c, "#ff4d5e", cx, cy + 3, 11, 5);
    R(c, "#ffd1d6", cx + 4, cy + 1, 2, 3);
    // ER entrance with canopy and light spilling onto the pavement.
    const ex = x0 + 44;
    R(c, "#3b4580", ex - 6, this.G - 22, 52, 3);
    ptext(c, "ER 24H", ex + 4, this.G - 30, "#ff6b7a");
    R(c, "#bfe8ff", ex, this.G - 19, 40, 19);
    R(c, "#8cc6e8", ex + 19, this.G - 19, 2, 19);
    dither(c, ex - 8, this.G, 56, 8, "#bfe8ff", 0.3);
  }

  private street(c: Ctx): void {
    const W = this.W;
    R(c, "#3a3f5e", 0, this.G, W, 9);
    R(c, "#565c80", 0, this.G + 9, W, 1);
    R(c, "#1d2136", 0, this.G + 10, W, this.H - this.G - 10);
    for (let x = 0; x < W; x += 18) R(c, "#8a8660", x, this.H - 10, 9, 1);
    for (const lx of this.lamps) {
      R(c, "#4a4f6e", lx, this.G - 26, 1, 26);
      R(c, "#4a4f6e", lx, this.G - 26, 5, 1);
      R(c, "#ffe9a8", lx + 4, this.G - 25, 2, 1);
      dither(c, lx - 4, this.G - 22, 16, 31, "#ffe9a8", 0.1);
    }
  }

  private ambulance(c: Ctx, now: number): void {
    const cycle = 17000;
    const p = (now % cycle) / 4200;
    if (p > 1) return;
    const x = Math.round(this.W + 40 - p * (this.W + 90));
    const y = this.G + 13;
    const red = Math.floor(now / 140) % 2 === 0;
    dither(c, x - 26, y + 2, 26, 8, "#ffe9a8", 0.25);
    R(c, "#eef0f6", x, y + 1, 9, 9);
    R(c, "#eef0f6", x + 9, y - 3, 22, 13);
    R(c, "#7fb3e8", x + 2, y + 2, 5, 3);
    R(c, "#e0414f", x, y + 6, 31, 1);
    R(c, "#e0414f", x + 19, y - 1, 1, 5);
    R(c, "#e0414f", x + 17, y + 1, 5, 1);
    R(c, INK, x + 4, y + 9, 4, 3);
    R(c, INK, x + 23, y + 9, 4, 3);
    R(c, red ? "#ff4d5e" : "#5a1a26", x + 11, y - 5, 3, 2);
    R(c, red ? "#1a2a5a" : "#4f8dff", x + 17, y - 5, 3, 2);
    dither(c, x + 9, y - 8, 14, 4, red ? "#ff4d5e" : "#4f8dff", 0.12);
  }

  private walkers(c: Ctx, now: number): void {
    const W = this.W;
    const lengths = SKITS.map((s) => skitLength(s, W));
    const total = lengths.reduce((a, b) => a + b, 0);
    let t = (now / 1000) % total;
    let idx = 0;
    while (t > (lengths[idx] ?? 0)) t -= lengths[idx++] ?? 0;
    const skit = SKITS[idx] ?? [];
    const drawn = skit
      .map((w) => {
        const d = (t - w.t0) * w.speed;
        const x = w.dir === "r" ? -20 + d : W + 4 - d;
        return { w, x };
      })
      .filter(({ w, x }) => t >= w.t0 && x > -24 && x < W + 8 && SPRITE[w.k]);
    for (const { w, x } of drawn) {
      const sp = SPRITE[w.k];
      if (!sp) continue;
      const fast = w.speed > 50;
      const f = Math.floor(now / (fast ? 90 : 160)) % 2;
      const img = f ? sp.f1 : sp.f0;
      const px = Math.round(x);
      const py = this.G - 12 - (f && fast ? 1 : 0);
      c.fillStyle = "rgba(0,0,0,.28)";
      c.fillRect(px + 3, this.G + 3, 10, 2);
      c.save();
      if (w.dir === "l") {
        c.translate(px + 16, 0);
        c.scale(-1, 1);
        c.drawImage(img, 0, py);
      } else c.drawImage(img, px, py);
      c.restore();
      if (w.bubble) bubble(c, px + 3, py - 12, w.bubble, now);
    }
  }
}
